import { FunctionComponent, h } from 'preact'
import { MutableRef, useContext, useEffect, useMemo, useRef, useState } from 'preact/hooks'
import { CSSTransition, TransitionGroup } from 'preact-transitioning'
import cls from 'classnames'
import { patchPublishedEntry } from 'scripts/service'
import { FieldEntry, FieldLayout, FormFieldType, NextFields, Page, RatingField, TriggerEvent } from 'scripts/type'
import FieldsContext, { FieldContextState } from '../Fields/fieldsContext'
import { useForceRefresh, useParentWindowSize } from 'scripts/hooks'
import BaseContext from 'contexts/base-context'
import SurveyContext from 'contexts/survey-context'

import { FieldComponentMap } from '../Fields/componentMapping'
import NextPage from '../Fields/components/FieldWrapper/NextPage'
import { charLength, getStorageKey, postMessage, Storage } from 'scripts/utils'
import { calcIsLastPage, findAllTriggeredFields, findErrorCodes, getFieldIsRequired } from '../Fields/utils'
import {
  useCalcPageHeight,
  useCurrentlyDisplayFieldsRelation,
  useFieldTransition,
  useHead,
  useScrollProps,
} from '../Fields/hooks'
import {
  FIELD_ENTER_ANIMATION_PREFIX,
  MAX_FIELD_LABEL_LENGTH_IN_ENGLISH,
  SDK_MODAL_TRANSITION_DURATION,
  SURVEY_ACTION_AREA_SELECTOR,
  SURVEY_FIELD_PAGE_SELECTOR,
} from 'scripts/constants'
import styles from './index.module.scss'
import { isDirectSubmittable } from 'components/business/SurveyForm/FieldsPage/utils/isDirectSubmittable'
import { ElementHeightGetter, FieldScrollArea } from 'components/ui'

type Props = {
  page: Page
  className?: string
  isActivePage: boolean
  onFieldHeightChange?: (height: number) => void
  onFieldChange?: (nextFields?: NextFields) => void
  onPageAnimationInStart?: () => void
  onPageAnimationInEnd?: () => void
  onPageAnimationShow?: () => void
  onPageAnimationOutStart?: () => void
  onPageAnimationOutEnd?: () => void
  onPageComplete?: (entryId: string, fieldsEntry: FieldEntry[]) => void
}

const FieldsPage: FunctionComponent<Props> = (props) => {
  const { refresh: forceRefresh, refreshDependence } = useForceRefresh()
  const { isPreview, urlToken, interactive = true, editingCode } = useContext(BaseContext)
  const { activePageIndex, pages, setActivePageIndex, setLastSubmitFieldCode } = useContext(SurveyContext)
  const {
    page,
    className,
    isActivePage,
    onFieldChange,
    onFieldHeightChange,
    onPageAnimationInStart,
    onPageAnimationInEnd,
    onPageAnimationShow,
    onPageAnimationOutStart,
    onPageAnimationOutEnd,
    onPageComplete,
  } = props
  const { nodes: fields, isLastPage } = page
  // 第一页的字段没有进场动画，但是需要有字段现隐动画
  // 默认值为当前是否第一页，第一页默认进场无动画
  // 配合下方的 useEffect 使用
  const [enableAppearAnimation, setEnableAppearAnimation] = useState(pages.indexOf(page) !== 0)
  const enableAnimation = interactive && enableAppearAnimation
  const firstField = page.nodes?.[0]
  const firstFieldHasImage = !!firstField.image
  const pageContentPaddingTop = firstFieldHasImage ? 0 : 20
  const isMultipleFields = fields.length > 1

  const showNextButton = useMemo(() => {
    return !isDirectSubmittable(fields)
  }, [fields])

  useEffect(() => {
    // enableAppearAnimation 是为了完成第一页字段初始化进场是不需要动画的需求，但是字段现隐切换是需要动画的
    // 所以会在一个 duration 后打开进场动画
    // 如果不是第一页，则不需要处理
    setTimeout(() => {
      if (enableAppearAnimation) {
        return
      }
      setEnableAppearAnimation(true)
    }, SDK_MODAL_TRANSITION_DURATION)
  }, [])

  const { windowSizeSm, windowSizeMd } = useParentWindowSize()
  const submitLoadingRef = useRef(false)
  const valuesRef = useRef<FieldEntry[]>([])
  const wrapperRef = useRef<HTMLDivElement>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const pageElementHeightMap = useRef<Record<string, number>>({})
  const { scrollToFieldPositionByCode } = useScrollProps(scrollAreaRef as MutableRef<HTMLDivElement>)

  const { currentlyDisplayFieldsRelationArr, updateCurrentlyDisplayFieldsRelationArr, willHiddenFieldCodes } =
    useCurrentlyDisplayFieldsRelation(fields)

  const currentlyDisplayFields = useMemo(
    () =>
      !interactive
        ? fields
        : fields.filter((field) => currentlyDisplayFieldsRelationArr.flat().some((f) => f.code === field.code)),
    [fields, currentlyDisplayFieldsRelationArr, interactive]
  )
  const [errFieldCodes, setErrFieldCodes] = useState(findErrorCodes(currentlyDisplayFields, valuesRef.current))

  useEffect(() => {
    setErrFieldCodes(findErrorCodes(currentlyDisplayFields, valuesRef.current))
  }, [currentlyDisplayFields])

  useEffect(() => {
    wrapperRef.current?.addEventListener('animationstart', (event) => {
      const isValidPageAnimateEvent = (event.target as HTMLDivElement)?.classList.contains(SURVEY_FIELD_PAGE_SELECTOR)
      const isEnterAnimation = event.animationName.startsWith(FIELD_ENTER_ANIMATION_PREFIX)
      if (isValidPageAnimateEvent) {
        if (isEnterAnimation) {
          onPageAnimationInStart?.()
        } else {
          onPageAnimationOutStart?.()
        }
      }
    })
    wrapperRef.current?.addEventListener('animationend', (event) => {
      const isValidPageAnimateEvent = (event.target as HTMLDivElement)?.classList.contains(SURVEY_FIELD_PAGE_SELECTOR)
      const isEnterAnimation = event.animationName.startsWith(FIELD_ENTER_ANIMATION_PREFIX)
      if (isValidPageAnimateEvent) {
        if (isEnterAnimation) {
          onPageAnimationInEnd?.()
          onPageAnimationShow?.()
        } else {
          onPageAnimationOutEnd?.()
        }
      }
    })
  }, [])

  useEffect(() => {
    isPreview && editingCode && setLastSubmitFieldCode?.(editingCode)
  }, [isPreview, editingCode])

  const updateCurrentPageValues = (value: FieldEntry) => {
    const currentlyDisplayCodes = currentlyDisplayFields.map(({ code }) => code)
    const filteredValues = valuesRef.current.filter((v) => v.code !== value.code)
    valuesRef.current = [...filteredValues, value].filter((value) => {
      return currentlyDisplayCodes.includes(value.code)
    })
    setErrFieldCodes(findErrorCodes(currentlyDisplayFields, valuesRef.current))
  }

  const closeWidget = () => {
    postMessage({ name: TriggerEvent.Close, data: { campaignToken: urlToken } })
  }

  const doSubmitRequest = async () => {
    const currentlyDisplayCodes = currentlyDisplayFields.map(({ code }) => code)
    const submitEntries = currentlyDisplayCodes.map((code) => {
      const entry = valuesRef.current.find((value) => value.code === code)
      return entry || { code, value: '' }
    })
    if (isPreview) {
      onFieldChange?.(submitEntries as any)
      setActivePageIndex?.(activePageIndex + 1)
    } else {
      submitLoadingRef.current = true
      try {
        const data = await patchPublishedEntry(submitEntries, urlToken)
        const publishedEntryId = Storage.getItem(getStorageKey('publishedEntryId', urlToken))
        onPageComplete?.(publishedEntryId, submitEntries)
        if (data.nodes.length === 0) {
          postCompleteMessage(urlToken, publishedEntryId)
          closeWidget()
        } else {
          onFieldChange?.(data)
          //获取的nextFields为空 或者 不为空但只有EndingField
          if (data.nodes.length === 0 || data.nodes[0].type === FormFieldType.EndingField) {
            postCompleteMessage(urlToken, publishedEntryId)
          }
          setActivePageIndex?.(activePageIndex + 1)
        }
      } catch (e) {
        closeWidget()
      } finally {
        submitLoadingRef.current = false
      }
    }
  }

  const postCompleteMessage = (campaignToken: string, entryId: string) => {
    postMessage({ name: TriggerEvent.Complete, data: { campaignToken, entryId } })
  }

  const isDynamicLastPage = useMemo(() => {
    return valuesRef.current.length === 0 ? undefined : calcIsLastPage(valuesRef.current, currentlyDisplayFields)
  }, [currentlyDisplayFields, valuesRef.current])

  const handleFieldValueChange = (code: string, value: FieldEntry['value']) => {
    setLastSubmitFieldCode?.(code)
    const fieldEntry: FieldEntry = {
      code,
      value,
    }
    updateCurrentPageValues(fieldEntry)
    const triggeredFields = findAllTriggeredFields(valuesRef.current, fields, code, value) || []
    if (showNextButton) {
      updateCurrentlyDisplayFieldsRelationArr(code, triggeredFields)
    }
  }

  const onFieldSubmit = (code: string, value: FieldEntry['value']) => {
    handleFieldValueChange(code, value)
    if (!showNextButton) {
      doSubmitRequest().then()
    }
  }

  const onMultipleQuestionNext = () => {
    if (submitLoadingRef.current) {
      return
    }
    if (errFieldCodes.length) {
      return scrollToFieldPositionByCode(errFieldCodes[0])
    }
    doSubmitRequest().then()
  }

  const defaultValue: FieldContextState = {
    fields,
    windowSizeSm,
    windowSizeMd,
    isLastPage,
    isMultipleFields,
    errFieldCodes,
    setErrFieldCodes,
  }

  const maxFieldLabelLength = useMemo(
    () =>
      Math.ceil(
        fields.reduce((memo, field) => {
          const length = charLength(field.label)
          if (field.type !== FormFieldType.RatingField) {
            return memo
          }
          if ((field as RatingField).layout !== FieldLayout.Compact) {
            return memo
          }
          if (length > MAX_FIELD_LABEL_LENGTH_IN_ENGLISH) {
            return MAX_FIELD_LABEL_LENGTH_IN_ENGLISH
          }
          if (length > memo) {
            return length
          }
          return memo
        }, 0) / 2
      ),
    [fields]
  )

  const fieldTransitionStyle = useFieldTransition(pageElementHeightMap.current, isPreview, interactive)

  useHead('fieldTransitionStyle', 'style', fieldTransitionStyle)
  useCalcPageHeight({
    isActivePage,
    currentlyDisplayFields,
    pageElementHeightMap: pageElementHeightMap.current!,
    refreshDependence: [refreshDependence, valuesRef.current],
    appendHeight: pageContentPaddingTop,
    onFieldHeightChange,
  })

  return (
    <div
      className={cls(SURVEY_FIELD_PAGE_SELECTOR, styles.fieldPage, className)}
      ref={wrapperRef}
      style={{ paddingTop: pageContentPaddingTop }}
    >
      <FieldsContext.Provider
        value={{
          ...defaultValue,
          refreshWidgetHeight: forceRefresh,
          syncFieldLabelLength: maxFieldLabelLength,
          isDynamicLastPage,
        }}
      >
        <FieldScrollArea ref={scrollAreaRef}>
          <TransitionGroup
            duration={SDK_MODAL_TRANSITION_DURATION}
            appear={enableAnimation}
            enter={enableAnimation}
            exit={enableAnimation}
          >
            {currentlyDisplayFields.map((field) => {
              const CurrentFieldComponent = FieldComponentMap[field.type]
              const willHidden = willHiddenFieldCodes.includes(field.code)
              const value = valuesRef.current.find((v) => v.code === field.code)?.value
              return (
                <CSSTransition key={field.code} classNames="fade">
                  <div className="field-transition" data-code={field.code}>
                    <ElementHeightGetter
                      onHeight={(height) => {
                        if (height) {
                          pageElementHeightMap.current[field.code] = height
                        }
                      }}
                    >
                      <CurrentFieldComponent
                        field={field}
                        required={getFieldIsRequired(field)}
                        onFieldChange={(value: string) => handleFieldValueChange(field.code, value)}
                        onFieldSubmit={(value: string) => onFieldSubmit(field.code, value)}
                        willHidden={willHidden}
                        value={value}
                      />
                    </ElementHeightGetter>
                  </div>
                </CSSTransition>
              )
            })}
          </TransitionGroup>

          <ElementHeightGetter
            onHeight={(height) => {
              pageElementHeightMap.current[SURVEY_ACTION_AREA_SELECTOR] = height ?? 0
            }}
          >
            {showNextButton && (
              <div className={styles.actions} id={SURVEY_ACTION_AREA_SELECTOR}>
                <NextPage persistClick disabled={!!errFieldCodes.length} onNext={onMultipleQuestionNext} />
              </div>
            )}
          </ElementHeightGetter>
        </FieldScrollArea>
      </FieldsContext.Provider>
    </div>
  )
}

export default FieldsPage
